/**
 * publicUrl — canonical public origin for anything a vendor shares.
 *
 * Vendors share links to their storefront, invoices, pay links, bookings,
 * forms, and vendor profiles. Those links MUST resolve to the customer-
 * facing brand (entreprenatives.com) — never leak the internal preview
 * host (e.g. `*.preview.emergentagent.com`) to the vendor's audience.
 *
 * Source of truth: `REACT_APP_PUBLIC_URL` in `frontend/.env`.
 * Fallback: strip any "emergent" host segment out of `window.location.origin`
 * and replace it with entreprenatives.com so we never leak internal hosts
 * in the (very unlikely) case the env var is missing at build time.
 *
 * Do NOT use `window.location.origin` directly in any share/copy flow.
 */

const CANONICAL = 'https://entreprenatives.com';

/** Public origin without a trailing slash — always customer-safe. */
export function getPublicOrigin() {
  const fromEnv = (process.env.REACT_APP_PUBLIC_URL || '').trim().replace(/\/$/, '');
  if (fromEnv) return fromEnv;
  // Defensive fallback — only used if env var is missing (shouldn't happen)
  if (typeof window !== 'undefined' && window.location?.origin) {
    const origin = window.location.origin;
    if (/emergent/i.test(origin)) return CANONICAL;
    return origin.replace(/\/$/, '');
  }
  return CANONICAL;
}

/**
 * Compose a full shareable URL from a path.
 * @param {string} path — leading slash optional (e.g. "site/kindred" or "/pay/abc").
 */
export function publicUrl(path = '') {
  const base = getPublicOrigin();
  if (!path) return base;
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
}

/**
 * Rewrite any URL string so its host is the canonical public origin.
 * Preserves path + query + hash. Non-URL input returned unchanged.
 */
export function canonicaliseShareUrl(input) {
  if (!input || typeof input !== 'string') return input;
  try {
    const u = new URL(input);
    const base = new URL(getPublicOrigin());
    u.protocol = base.protocol;
    u.host = base.host;
    return u.toString();
  } catch {
    return input;
  }
}
